import {
    Component,
    Inject,
    OnInit,
    ViewEncapsulation,
    HostBinding,
    HostListener,
    inject,
    ViewChild,
    ElementRef,
    AfterViewInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import {
    debounceTime,
    distinctUntilChanged,
    Observable,
    OperatorFunction,
    Subject,
    switchMap,
    tap,
    catchError,
    of,
    map,
} from 'rxjs';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

import { ResetPasswordModal } from './modals/reset-password/reset-password.component';
import { NgbModal, NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';

import { environment } from 'src/environments/environment';

import { AuthService } from 'src/app/services/AuthService';
import { ClearService } from 'src/app/services/ClearService';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { PlausibleAnalyticsService } from 'src/app/services/plausible-analytics.service';
import { SearchService } from 'src/app/services/search.service';
import { SolarService } from 'src/app/services/cadastre-solaire/cadastre-solaire.service';
import { ThemeService } from 'src/app/services/ThemeService';
import { UsefulService } from 'src/app/services/UsefulService';

import { CadastreSolaireTutorialModalComponent } from 'src/app/controllers/main/components/module-cadastre-solaire/modals/tutorial/cadastre-solaire-tutorial-modal.component';
import { CadastreSolaireContactModalComponent } from 'src/app/controllers/main/components/module-cadastre-solaire/modals/contact/cadastre-solaire-contact-modal.component';
import { TerritoryScaleId } from 'src/app/shared/enums/territory-scale.enum';

@Component({
    selector: 'login',
    templateUrl: environment.login,
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class LoginComponent implements OnInit, AfterViewInit {
    @HostBinding('style.width.px') esterelHeaderHeight!: string;
    public isAtLeastMedium!: boolean;

    @ViewChild('svgMap') svgMap: ElementRef;

    showNavigationArrows = false;
    showNavigationIndicators = false;
    pauseOnFocus = false;
    pauseOnHover = false;

    userId: number;
    formModal: any;

    address: any = {
        label: '',
    };
    userCredentials = {
        login: '',
        password: '',
    };
    focus$ = new Subject<string>();
    click$ = new Subject<string>();
    isSearching: boolean;
    isAnyResultFound: boolean;

    isFetchingAddress = false;
    msg = {
        errorConnection: false,
        errorback: false,
        errorMessage: '',
    };
    headerTemplate = environment.header;

    showMobileUi: boolean = false;

    public tooltipVisible = false;
    public tooltipCommune = '';
    public tooltipX = 0;
    public tooltipY = 0;

    constructor(
        private modalService: NgbModal,
        private notification: ToastrService,
        private offcanvasService: NgbOffcanvas,
        private router: Router,
        @Inject(AuthService) private authService: AuthService,
        @Inject(ClearService) private clearService: ClearService,
        @Inject(LocalStorageService) private localStorageService: LocalStorageService,
        @Inject(PlausibleAnalyticsService)
        private plausibleAnalyticsService: PlausibleAnalyticsService,
        @Inject(SearchService) private searchService: SearchService,
        @Inject(SolarService) private solarService: SolarService,
        @Inject(ThemeService) private themeService: ThemeService,
        @Inject(UsefulService) private usefulService: UsefulService,
    ) {
        this.showMobileUi = this.usefulService.showMobileUi;
        inject(BreakpointObserver)
            .observe([Breakpoints.Medium, Breakpoints.Large, Breakpoints.XLarge])
            .pipe()
            .subscribe((result) => {
                const matches = result.matches;
                this.isAtLeastMedium = matches;
            });
    }

    async ngOnInit(): Promise<void> {
        this.clearService.clearApp();
        const profileName = environment.name;
        this.localStorageService.set('profile', profileName);

        if (profileName !== 'siterre') {
            const token = environment.token;
            this.authService.setToken(token);

            await this._setProfileParameters();

            this.themeService.get();
            this.themeService.save();
            this.themeService.apply();

            const isSolar = environment.isSolar;
            const isLandingPageDefined = environment.isLandingPageDefined;

            if (!isLandingPageDefined) {
                if (isSolar) {
                    this.accessToMainForSolar();
                } else {
                    this._setPath('/main');
                }
            }
        }

        this.setEsterelHeaderHeight();
    }

    public onMapMouseMove(event: MouseEvent): void {
        const element = event.target as Element;

        const commune = element.closest(
            'path[data-name]',
        ) as SVGPathElement | null;

        if (!commune) {
            this.hideCommuneTooltip();
            return;
        }

        const svg = event.currentTarget as SVGSVGElement;
        const svgPosition = svg.getBoundingClientRect();

        this.tooltipCommune = commune.dataset['name'] ?? '';
        this.tooltipX = event.clientX - svgPosition.left + 6;
        this.tooltipY = event.clientY - svgPosition.top - 6;
        this.tooltipVisible = true;
    }

    public hideCommuneTooltip(): void {
        this.tooltipVisible = false;
    }

    public ngAfterViewInit(): void {
        if (this.svgMap) {
            const paths = this.svgMap.nativeElement.querySelectorAll('#communes path');

            paths.forEach((path) => {
                path.addEventListener('click', (event) => {
                    this._onMapClick(event.target);
                });
                path.addEventListener('mouseenter', (event) => {
                    this._onMapHover(event.target, true);
                });
                path.addEventListener('mouseleave', (event) => {
                    this._onMapHover(event.target, false);
                });
            });
        }
    }

    @HostListener('window:resize')
    onResize(): void {
        this.setEsterelHeaderHeight();
    }

    private setEsterelHeaderHeight(): void {
        const width = window.innerWidth;
        const height = window.innerHeight;
        const headerHeight = Math.min(width / 6, height - 490);
        this.esterelHeaderHeight = `${headerHeight}px`;
    }

    formatter(x: any) {
        return x.label;
    }

    search: OperatorFunction<string, readonly string[]> = (text$: Observable<string>) =>
        text$.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            tap(() => (this.isSearching = true)),
            switchMap((term) => {
                return this.searchService.searchAddress(term).pipe(
                    map((response) => {
                        this.isAnyResultFound = response.length > 0;
                        if (this.isAnyResultFound && term.length >= 3) {
                            response.push({ isLast: true });
                        }
                        return response;
                    }),
                    catchError((error) => {
                        console.error('Error search', error);
                        this.notification.warning(
                            "Une erreur s'est produite. Impossible d'afficher la liste des adresses.",
                        );
                        this.isAnyResultFound = false;
                        return of([]);
                    }),
                );
            }),
            tap(() => (this.isSearching = false)),
        );

    private _setPath(path: string) {
        this.router.navigate([path]);
    }

    pressEnter(keyEvent: KeyboardEvent) {
        if (keyEvent.key === 'Enter' && Object.values(this.userCredentials).every((x) => !!x)) {
            this.validate();
        }
    }

    async validate() {
        if (!Object.values(this.userCredentials).every((x) => !!x)) return;

        try {
            await this.authService.logIn(this.userCredentials);
            await this.authService.getUserPreferences();
            this._setPath('/main');
        } catch (error) {
            console.error('Error validate', error);
            this.msg.errorConnection = true;
            this.msg.errorMessage = error.error.message;
        }
    }

    async updateUserPassword(resetUserId: number) {
        this.userId = resetUserId;
        const modalReference = this.modalService.open(ResetPasswordModal);
        modalReference.componentInstance.userId = resetUserId;
        await modalReference.result.then(
            async () => {
                return;
            },
            () => {},
        );
    }

    resetErrorMessage() {
        this.msg.errorback = false;
        this.msg.errorConnection = false;
    }

    private async _setProfileParameters() {
        try {
            const user = await this.authService.getUserPreferences(true);
            this.authService.setUserInfo(user);
        } catch (error) {
            console.error('Error setProfileParameters', error);
            this.notification.error(
                "Une erreur est survenue lors de l'initialisation de l'application.",
            );
        }
    }

    selectSolarAddress(address: any) {
        const user = this.localStorageService.get('user');
        this.plausibleAnalyticsService.trackEvent('Accès', { methode: 'Adresse' });

        this.plausibleAnalyticsService.trackEvent('Adresse', {
            utilisateur_id: user.id,
            Adresse: address.properties?.label,
            Commune: address.properties?.city,
            Source: 'Recherche',
        });

        this.solarService.setAddress(address);
        this.address = address;
        const lat = address.geometry.coordinates[1];
        const lng = address.geometry.coordinates[0];
        const label = address.properties?.label;

        this._saveSolarConnection(lat, lng, label);
        this._setPath('/main');
    }

    accessToMainForSolar() {
        this.plausibleAnalyticsService.trackEvent('Accès', { Méthode: 'Direct' });

        const preferences = this.localStorageService.get('preferences');
        const lat = preferences.territory.latitude;
        const lng = preferences.territory.longitude;
        const label = preferences.territory.territoryNames[0];

        this._saveSolarConnection(lat, lng, label);
        this._setPath('/main');
    }

    private _saveSolarConnection(latitude: number, longitude: number, label: string) {
        this.solarService.saveSolarLogIn(label, latitude, longitude);
    }

    async geolocalizeMe() {
        const successCallback = async (latitude: number, longitude: number) => {
            try {
                const features = await this.searchService.reverseAddress(latitude, longitude);
                const feature = features[0];
                this.solarService.setAddress(feature);

                const label = feature.properties.label;
                this._saveSolarConnection(latitude, longitude, label);

                const user = this.localStorageService.get('user');
                this.plausibleAnalyticsService.trackEvent('Adresse', {
                    utilisateur_id: user.id,
                    Adresse: feature.properties?.label,
                    Commune: feature.properties?.city,
                    Source: 'Géolocalisation',
                });

                this.plausibleAnalyticsService.trackEvent('Accès', { Méthode: 'Géolocalisation' });

                this._setPath('/main');
            } catch (error) {
                console.error('Error geolocalizeMe', error);
                throw error;
            }
        };
        const errorCallback = () => this.notification.error('La géolocalisation est indisponible.');

        try {
            this.searchService.readGeolocation(successCallback, errorCallback);
        } catch (error) {
            console.error('Error geolocalizeMe', error);
            this.notification.error('La géolocalisation est indisponible.');
        }
    }

    openOffCanvasSidebar(content: any, cadastreName: string) {
        this.offcanvasService.open(content, {
            position: 'end',
            panelClass: `${cadastreName}-offcanvas`,
            backdropClass: `${cadastreName}-backdrop`,
        });
    }

    openTutorialModal() {
        const profileName = environment.name;

        this.modalService.dismissAll();
        const modalRef = this.modalService.open(CadastreSolaireTutorialModalComponent, {
            size: 'xl',
            windowClass: `tutorial-modal ${profileName}`,
            centered: true,
        });

        modalRef.componentInstance.separator = profileName != 'cadastre_esterel';
    }

    openCsgpContactModal() {
        const profileName = environment.name;

        const modalRef = this.modalService.open(CadastreSolaireContactModalComponent, {
            modalDialogClass: profileName,
        });
        modalRef.result.then(
            (result) => {},
            () => {},
        );
    }

    private _onMapHover(event: any, addClass: boolean) {
        const inseeCode = event.id;

        if (!inseeCode) {
            return;
        }

        const cityNameElement = this.svgMap.nativeElement.getElementById(`t_${inseeCode}`);

        if (!cityNameElement) {
            return;
        }

        if (addClass) {
            cityNameElement.classList.add('active');
        } else {
            cityNameElement.classList.remove('active');
        }
    }

    private _onMapClick(event) {
        const inseeCode = event.id;

        if (!inseeCode) {
            return;
        }

        const lomiaInitTerritories = environment.lomiaInitTerritories;
        if (!lomiaInitTerritories) {
            return;
        }

        const city = lomiaInitTerritories[inseeCode];

        if (!city) {
            return;
        }

        if (city.url) {
            window.location.replace(city.url);
        } else {
            const preferences = this.localStorageService.get('preferences') || {};
            preferences.territory = {
                drawTerritory: true,
                year: 2021,
                scaleTypeId: TerritoryScaleId.CITY,
                granularityTypeId: 1,
                territoryIds: [inseeCode],
                latitude: city.latitude,
                longitude: city.longitude,
                territoryNames: [city.name],
            };

            this.localStorageService.set('preferences', preferences);
            this.accessToMainForSolar();
        }
    }
}
